import React from "react";
import {FormattedMessage, useIntl} from "react-intl";

import {setAiRenderOptions, setAiFeatureAreas} from "../aiActions";
import {useAppDispatch, useAppSelector} from "../../../storeHooks";
import {getCommonVisualizerType} from "../../../config";
import {getCurrentLayout} from "../../appLayout";
import {ALL_SHOWER_AREA_GROUPS, ALL_VANITY_AREA_GROUPS} from "../../look/core/constants";
import UiIcon from "../../ui/icons/uiIcon";
import UiPopupAIContentFeaturesAreaDropdown from "./UiPopupAIContentFeaturesAreaDropdown";

export const FEATURE_AREAS: {featureArea: string, partIds: string[]}[] = [
  {featureArea: "Wet Area", partIds: [...new Set<string>(ALL_SHOWER_AREA_GROUPS)]},
  {featureArea: "Vanity Area", partIds: [...new Set<string>(ALL_VANITY_AREA_GROUPS)]},
];

export default function UiPopupAIContentFeaturesAreas() {

  const aiRenderOptions = useAppSelector(state => state.aiState.aiRenderOptions);
  const aiFeatureAreas = useAppSelector(state => state.aiState.aiFeatureAreas);
  const fabricatorData = useAppSelector(state => state.fabricatorState);

  const {aiFeatureAreaEnabled} = aiRenderOptions;

  const intl = useIntl();
  const dispatch = useAppDispatch();
  const visualizerType = getCommonVisualizerType();

  if (visualizerType !== "bathroom") {
    return null;
  }

  const layout = getCurrentLayout();

  const toggleFeatureArea = (featureArea: string, availablePartIds: string[]) => {
    const isSelected = !!aiFeatureAreas?.[featureArea]?.length;
    const layers = layout.getLayers();
    const currentActivePartIds = availablePartIds.filter(partId => {
      return layers.some(l => l.partId === partId)
    });
    dispatch(setAiFeatureAreas({
      [featureArea]: isSelected ? [] : currentActivePartIds,
    }));
  };

  const toggleFeatureGroup = (featureArea: string, partId: string) => {
    const selectedPartIds: string[] = aiFeatureAreas?.[featureArea] || [];
    dispatch(setAiFeatureAreas({
      [featureArea]: selectedPartIds.includes(partId)
        ? selectedPartIds.filter(id => id !== partId)
        : [...selectedPartIds, partId],
    }));
  };

  return (
    <div className="u-mb-1 flex flex--top flex--start u-gap-1">
      <div className={"flex flex--column flex--start flex--top"}>
        <span className="ui-radio-label nowrap">Attach Additional Areas:</span>
        <button className={"ui-radio ui-radio--small" + (aiFeatureAreaEnabled ? " ui-radio--checked" : "")}
                onClick={() => dispatch(setAiRenderOptions({
                  "aiFeatureAreaEnabled": !aiFeatureAreaEnabled}
                ))}>
          <span className="ui-radio-circle"/>
          <span className="ui-radio-text ui-radio-text--yes"><FormattedMessage id="radioButton.on"/></span>
          <span className="ui-radio-text ui-radio-text--no"><FormattedMessage id="radioButton.off"/></span>
        </button>
      </div>
      {aiFeatureAreaEnabled ? (
        <div className="flex flex--start flex--top u-gap-1">
          {FEATURE_AREAS.map(({featureArea, partIds}) => {
            const featureGroupItems = partIds.map(partId => {
              return {partId, featureGroup: layout?.getFeatureGroupByPartId(partId)}
            }).filter(fg=> fg.featureGroup);
            const isSelected = !!aiFeatureAreas?.[featureArea]?.length;
            return (
              <div key={featureArea} className="flex flex--column flex--start flex--top u-gap-025 min-w-[132px]">
                <div className="flex flex--center flex--start">
                  <div className={"ui-checkbox ui-checkbox--small"
                    + (isSelected ? " ui-checkbox--checked-svg" : "")}
                       onClick={() => toggleFeatureArea(featureArea, featureGroupItems.map(item => item.partId))}>
                    <div className="ui-checkbox__svg">
                      <UiIcon icon="checkmark"/>
                    </div>
                  </div>
                  <div className="ui-checkbox__label"
                       onClick={() => toggleFeatureArea(featureArea, featureGroupItems.map(item => item.partId))}>
                    <span className="color-primary bold">{featureArea}</span>
                  </div>
                </div>
                {isSelected ? (
                  <UiPopupAIContentFeaturesAreaDropdown
                    featureArea={featureArea}
                    featureGroupItems={featureGroupItems}
                    selectedPartIds={aiFeatureAreas?.[featureArea] || []}
                    onToggle={toggleFeatureGroup}
                    intl={intl}
                    fabricatorData={fabricatorData}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  )
}
